<template>
  <DraggableModalWrapper :is-visible="isVisible">
    <div v-if="isWarmTransferEnabled" class="flex p-2 border_bottom">
      <CallParticipants source="call_dialog" />
    </div>

    <div class="flex flex-col items-center gap-2 p-2 border_bottom">
      <span class="text-sm my-2">
        <template v-if="isIncoming && callStatus === 'pending'">
          Incoming Call
        </template>
        <template
          v-else-if="callStatus === 'pending' || callStatus === 'ringing'"
        >
          Calling...
        </template>
        <template v-else-if="callStatus === 'connecting'">
          Connecting...
        </template>
        <template v-else-if="callStatus === 'open'">
          {{ callDuration }}
        </template>
        <template v-else-if="callStatus === 'reconnecting'">
          Reconnecting...
        </template>
        <template v-else-if="callStatus === 'closed'"> Disconnected </template>
      </span>

      <CallDialogDetails
        source="call_dialog"
        :is-incoming="isIncoming"
        :call-details="callDetails"
        :contact-details="contactDetails"
      />

      <CallActions
        v-if="!isIncoming || callStatus !== 'pending'"
        @mousedown.stop
        @touchstart.stop
      />

      <CompleteTransferButton source="call_dialog" />
      <CancelTransferButton source="call_dialog" />
    </div>

    <div
      :class="[
        'flex flex-row justify-between items-center p-2',
        isIncoming ? 'gap-12' : '',
      ]"
    >
      <div class="flex flex-row items-center text-[13px] gap-1">
        <ThemeImage
          class="size-4"
          :class="{ 'rotate-180': isIncoming }"
          :icon="imageNames.VIA_GREY"
          alt="via_icon"
        />
        <span class="secondary_color">via</span>
        {{ callDetails.From }}
      </div>

      <div
        v-if="isIncoming && callStatus === 'pending'"
        class="flex flex-row gap-1"
      >
        <a-button
          class="end_call"
          @click="rejectCall"
          @mousedown.stop
          @touchstart.stop
        >
          <ThemeImage
            class="size-4 rotate-[135deg]"
            :icon="imageNames.DIALER_PHONE_ICON"
            alt="end_call_icon"
          />
        </a-button>

        <a-button
          class="receive_call"
          @click="answerCall"
          @mousedown.stop
          @touchstart.stop
        >
          <ThemeImage
            class="size-4"
            :icon="imageNames.DIALER_PHONE_ICON"
            alt="receive_call_icon"
          />
        </a-button>
      </div>

      <a-button
        v-else
        class="end_call"
        @click.stop="handleEndCall"
        @mousedown.stop
        @touchstart.stop
        :disabled="callStatus === 'closed'"
      >
        <ThemeImage
          class="size-4 rotate-[135deg]"
          :icon="imageNames.DIALER_PHONE_ICON"
          alt="end_call_icon"
        />
      </a-button>
    </div>
  </DraggableModalWrapper>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useStore } from "vuex";
import { imageNames } from "@/src/constants/imageConstants.js";
import { callStatuses, dialerTypes } from "@/src/constants/callConstants.js";
import {
  hangupCall,
  getCurrentConnection,
  getDeviceStatus,
  answerIncomingCall,
  rejectIncomingCall,
  getIncomingConnection,
  hangupIncomingCall,
  toAddCallNote,
  toGetPhoneNumbers,
} from "@/src/utils/callsUtils.js";
import eventBus from "@/src/utils/eventBus.js";
import CallActions from "@/src/components/telephony_components/actions/CallActions.vue";
import CallParticipants from "@/src/views/calls/call_widget/CallParticipants.vue";
import CallDialogDetails from "@/src/components/telephony_components/call_details/CallDialogDetails.vue";
import DraggableModalWrapper from "@/src/components/telephony_components/modules/DraggableModalWrapper.vue";
import CompleteTransferButton from "@/src/components/telephony_components/actions/CompleteTransferButton.vue";
import CancelTransferButton from "@/src/components/telephony_components/actions/CancelTransferButton.vue";

const props = defineProps({
  callDetails: { type: Object, required: true },
  isIncoming: { type: Boolean, default: false },
});

const store = useStore();
const callDuration = ref("00:00");
const callStatus = ref("pending");
const isConnecting = ref(false);
const phoneNumbers = ref(null);

const contactDetails = computed(() => {
  if (store.getters["calls/isCampaign"]) {
    return store.getters["calls/entityDetails"];
  } else {
    return props.isIncoming
      ? store.getters["calls/incomingContactDetails"]
      : store.getters["calls/outgoingContactDetails"];
  }
});

const isWarmTransferEnabled = computed(
  () => store.getters["conferenceCall/isWarmTransfer"],
);

const isCampaignCall = computed(() => store.getters["calls/isCampaign"]);

const isParallelCallCampaign = computed(
  () =>
    store.getters["callsCampaign/campaignType"] === dialerTypes.PARALLEL_DIALER,
);

const isVisible = ref(!isParallelCallCampaign.value);

function updateVisibility() {
  isVisible.value = !(isParallelCallCampaign.value && isCampaignCall.value);
}

let timerStartTime = null;
let timerInterval = null;

const updateCallDuration = () => {
  try {
    if (timerStartTime) {
      const duration = Math.floor((Date.now() - timerStartTime) / 1000);
      const minutes = Math.floor(duration / 60)
        .toString()
        .padStart(2, "0");
      const seconds = (duration % 60).toString().padStart(2, "0");
      callDuration.value = `${minutes}:${seconds}`;
    }
  } catch (error) {
    console.error("An error occurred while updating call duration: ", error);
  }
};

const startTimer = () => {
  try {
    if (!timerStartTime) {
      timerStartTime = Date.now();
      if (timerInterval) {
        clearInterval(timerInterval);
      }
      timerInterval = setInterval(updateCallDuration, 1000);
    }
  } catch (error) {
    console.error("An error occurred while starting timer: ", error);
  }
};

const stopTimer = () => {
  try {
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    timerStartTime = null;
    callDuration.value = "00:00";
  } catch (error) {
    console.error("An error occurred while stoping timer: ", error);
  }
};

const restartTimer = () => {
  try {
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    timerStartTime = null;
    callDuration.value = "00:00";
    timerStartTime = Date.now();
    timerInterval = setInterval(updateCallDuration, 1000);
  } catch (error) {
    console.error("An error occurred while restarting timer: ", error);
  }
};

const updateCallStatus = () => {
  try {
    const currentConnection = getCurrentConnection();
    const incomingConnection = getIncomingConnection();
    const activeConnection = props.isIncoming
      ? incomingConnection || currentConnection
      : currentConnection;

    if (activeConnection) {
      const newStatus = typeof activeConnection.status === 'function' 
        ? activeConnection.status() 
        : activeConnection.status;
        
      if (callStatus.value !== newStatus) {
        callStatus.value = newStatus;
        store.dispatch("calls/updateCallStatus", newStatus);
        
        if (newStatus === "closed") {
          stopTimer();
          handleEndCallEvents();
        } else if (newStatus === callStatuses.OPEN || newStatus === "open") {
          if (!timerStartTime) {
            startTimer();
          }
          eventBus.emit("call-open");
        }
      }
    } else if (callStatus.value !== "closed" && callStatus.value !== "pending") {
      callStatus.value = "closed";
      stopTimer();
      handleEndCallEvents();
    }
  } catch (error) {
    console.error("An error occurred while updating call status: ", error);
  }
};

const answerCall = async () => {
  if (isConnecting.value) return;

  try {
    isConnecting.value = true;
    const connection = await answerIncomingCall();
    if (connection) {
      callStatus.value = "open";
      startTimer();
      setupConnectionListeners(connection);
      store.commit("calls/SET_IS_CALL_CONNECTED", true);
      store.commit("calls/UPDATE_CALL_STATUS", { status: "open" });
    } else {
      cleanupCall();
      await handleEndCallEvents();
    }
  } catch (error) {
    console.error("Error answering call:", error);
    cleanupCall();
    await handleEndCallEvents();
  } finally {
    isConnecting.value = false;
  }
};

const rejectCall = async () => {
  try {
    await rejectIncomingCall();
  } catch (error) {
    console.error("An error occurred while rejecting call: ", error);
  } finally {
    cleanupCall();
    await handleEndCallEvents();
  }
};

const handleEndCallEvents = async () => {
  try {
    await store.dispatch(
      props.isIncoming ? "calls/endIncomingCall" : "calls/endOutgoingCall",
    );
    await store.dispatch("conferenceCall/endConference");
  } catch (error) {
    console.error("An error occurred while handling end call events: ", error);
  }
};

const handleEndCall = () => {
  try {
    const currentConnection = getCurrentConnection();
    const incomingConnection = getIncomingConnection();

    console.log("Current connection:", currentConnection);
    console.log("Incoming connection:", incomingConnection);

    if (currentConnection?.parameters?.CallSid) {
      toAddCallNote(
        currentConnection.parameters.CallSid,
        store.getters["calls/currentCallNote"],
      );
    }

    const deviceStatus = getDeviceStatus();
    if (deviceStatus.hasActiveCall || deviceStatus.hasIncomingCall) {
      hangupCall();
    }

    callStatus.value = "closed";
    stopTimer();
    cleanupCall();
    handleEndCallEvents();
  } catch (error) {
    console.error("An error occurred while handling end call: ", error);
  }
};

const handleCallCampaignPickup = async () => {
  try {
    if (isParallelCallCampaign.value) {
      restartTimer();
      isVisible.value = true;
    }
  } catch (error) {
    console.error(
      "An error occurred while handline call campaign pickup: ",
      error,
    );
  }
};

const handleCallDialogMounted = async () => {
  try {
    await handleGetPhoneNumbers();
  } catch (error) {
    console.error(
      "An error occurred while handling call dialog mounted: ",
      error,
    );
  }
};

const handleGetPhoneNumbers = async () => {
  try {
    phoneNumbers.value = await toGetPhoneNumbers();
  } catch (error) {
    console.error(
      "An error occurred while handling get phone numbers: ",
      error,
    );
  }
};

const cleanupCall = () => {
  try {
    stopTimer();
    isConnecting.value = false;
    callStatus.value = "closed";
  } catch (error) {
    console.log("An error occurred while cleaning up call: ", error);
  }
};

const setupConnectionListeners = (connection) => {
  try {
    if (connection) {
      const events = [
        "accept",
        "answered",
        "disconnect",
        "reconnecting",
        "reconnected",
        "ringing",
        "cancel",
      ];
      events.forEach((event) => {
        connection.on(event, () => {
          updateCallStatus();
          if (event === "accept") {
            startTimer();
          } else if (event === "disconnect") {
            stopTimer();
            handleEndCallEvents();
          }
        });
      });
    }
  } catch (error) {
    console.error(
      "An error occurred while setting up connection listeners: ",
      error,
    );
  }
};

watch([isParallelCallCampaign, isCampaignCall], () => {
  updateVisibility();
});

watch(callStatus, (newStatus) => {
  console.log(`Call status changed to ${newStatus}`);

  if (newStatus === callStatuses.OPEN || newStatus === "open") {
    startTimer();
    setTimeout(() => {
      eventBus.emit("call-open");
    }, 600);
  }

  if (newStatus === "closed") {
    stopTimer();
    handleEndCallEvents();
  }
});

onMounted(() => {
  handleCallDialogMounted();
  updateVisibility();

  if (!props.isIncoming) {
    const currentConnection = getCurrentConnection();
    if (currentConnection) {
      updateCallStatus();
      setupConnectionListeners(currentConnection);
    }

    const statusCheckInterval = setInterval(() => {
      updateCallStatus();
    }, 1000);

    onUnmounted(() => {
      stopTimer();
      clearInterval(statusCheckInterval);
      const deviceStatus = getDeviceStatus();
      if (deviceStatus.hasActiveCall) {
        hangupCall();
        store.dispatch("calls/endOutgoingCall");
      }
    });
  } else {
    const checkForIncomingConnection = () => {
      const incomingConnection = getIncomingConnection();
      if (incomingConnection) {
        setupConnectionListeners(incomingConnection);
        return true;
      }
      return false;
    };
    
    if (!checkForIncomingConnection()) {
      const connectionCheckInterval = setInterval(() => {
        if (checkForIncomingConnection()) {
          clearInterval(connectionCheckInterval);
        }
      }, 500);
      
      onUnmounted(() => {
        clearInterval(connectionCheckInterval);
      });
    }
    
    const statusCheckInterval = setInterval(() => {
      updateCallStatus();
    }, 1000);
    
    onUnmounted(() => {
      stopTimer();
      clearInterval(statusCheckInterval);
    });
  }

  eventBus.on("call-campaign-pickup", handleCallCampaignPickup);
});

onUnmounted(() => {
  eventBus.off("call-campaign-pickup", handleCallCampaignPickup);
});
</script>
